// ./src/components/features/CreateEventWizard/wizardStarters.ts
//
// One-tap starters for the wizard's planning steps — Timeline, Tasks, Budget (ROADMAP A2b #62 P6(e)). A starter only
// OPENS the feature page's own drawer with a name filled in; the host still reviews and saves it there, so a starter can
// never make a row the drawer would not.
//
// ⓘ These are the few EVERY event gets. A type's OWN ideas (a wedding's "Cake cutting", a potluck's "Dessert") come from
//   the catalog — `offers[].options.ideas`, seeded in the API's type catalog (#62 P6(h)) — and REPLACE these when the
//   type has some (`startersFor`): a dozen buttons above a short list is not a starter, it is a second list.
// ⚠ Every `icon` must exist in the generated AppIcon registry, or it renders a silent question mark (`check-event-setup`
//   reads this file against it).

export interface WizardNameStarter {
  key: string;
  label: string;
  icon: string;
}

/** The run of show: before, during and after, in the order they happen. */
export const TIMELINE_STARTERS: readonly WizardNameStarter[] = [
  { key: 'setup', label: 'Set-up', icon: 'Tools' },
  { key: 'doors', label: 'Doors open', icon: 'DoorEnter' },
  { key: 'welcome', label: 'Welcome', icon: 'Microphone' },
  { key: 'food', label: 'Food & drinks', icon: 'ToolsKitchen2' },
  { key: 'wrapup', label: 'Wrap-up', icon: 'Flag' },
  { key: 'teardown', label: 'Clean-up', icon: 'Home' },
];

/** What most hosts have to do before the day. */
export const TASK_STARTERS: readonly WizardNameStarter[] = [
  { key: 'venue', label: 'Book the venue', icon: 'Building' },
  { key: 'food', label: 'Order food & drinks', icon: 'ToolsKitchen2' },
  { key: 'decor', label: 'Buy decorations', icon: 'Balloon' },
  { key: 'music', label: 'Arrange music', icon: 'Music' },
  { key: 'photos', label: 'Book a photographer', icon: 'Camera' },
  { key: 'reminder', label: 'Send a reminder to guests', icon: 'Mail' },
];

/** The usual costs. */
export const BUDGET_STARTERS: readonly WizardNameStarter[] = [
  { key: 'venue', label: 'Venue', icon: 'Building' },
  { key: 'food', label: 'Food & drinks', icon: 'ToolsKitchen2' },
  { key: 'decor', label: 'Decorations', icon: 'Balloon' },
  { key: 'entertainment', label: 'Entertainment', icon: 'Music' },
  { key: 'photos', label: 'Photography', icon: 'Camera' },
  { key: 'printing', label: 'Printing', icon: 'Printer' },
];

/**
 * The starters a step shows: the TYPE's own ideas when it has any (as names, with the step's icon), else the generic
 * ones. Pure — `check-event-setup` runs it.
 */
export const startersFor = (
  typeIdeas: readonly string[] | null | undefined,
  generic: readonly WizardNameStarter[],
  icon: string,
): WizardNameStarter[] => {
  const own = (typeIdeas ?? []).map((label) => label.trim()).filter(Boolean);

  return own.length > 0 ? own.map((label, i) => ({ key: `idea-${i}`, label, icon })) : [...generic];
};

/**
 * The starters not yet on the event — one whose name a row already has (any case, trimmed) is hidden, so a tapped
 * starter disappears once its row is saved.
 */
export const unusedStarters = (starters: readonly WizardNameStarter[], names: Array<string | null | undefined>): WizardNameStarter[] => {
  const taken = new Set(names.map((n) => (n ?? '').trim().toLowerCase()).filter(Boolean));

  return starters.filter((s) => !taken.has(s.label.toLowerCase()));
};
